import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import StudentLayout from "@/components/StudentLayout";
import api from "@/utils/api";
import { ArrowLeft, BookOpen, FileText, StickyNote, Image, Download, ExternalLink, Video, Lock } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ModuleView() {
  const { id } = useParams();
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    api.get(`/modules/${id}`).then((r) => setMod(r.data)).catch((e) => {
      if (e.response?.status === 403) setAccessDenied(true);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-[#5C3A2E]/50 font-body">Loading module...</div>
        </div>
      </StudentLayout>
    );
  }

  if (!mod) {
    return (
      <StudentLayout>
        <div className="text-center py-20">
          {accessDenied ? (
            <>
              <Lock className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/50 font-medium">This module hasn't been unlocked yet</p>
              <p className="text-[#2B2B2B]/30 text-sm mt-1">You'll gain access when your course progresses to this module.</p>
            </>
          ) : (
            <p className="text-[#2B2B2B]/50">Module not found</p>
          )}
          <Link to="/" className="text-[#5C3A2E] hover:text-[#C6A969] text-sm mt-4 inline-block">Back to dashboard</Link>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div data-testid="module-view" className="space-y-8 animate-fade-in-up">
        {/* Back link */}
        <Link to="/" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-sm text-[#2B2B2B]/50 hover:text-[#5C3A2E] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Your Studio
        </Link>

        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-[#C6A969]" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#C6A969] font-medium">Current Module</p>
          </div>
          <h1 data-testid="module-title" className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#5C3A2E] font-semibold tracking-tight">
            {mod.title}
          </h1>
          {mod.description && (
            <p className="text-[#2B2B2B]/60 mt-3 text-base sm:text-lg leading-relaxed max-w-3xl">{mod.description}</p>
          )}
        </div>

        {/* What You'll Learn */}
        {mod.what_youll_learn && (
          <div data-testid="module-learn-section" className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-[#5C3A2E]" />
              <h2 className="font-heading text-xl sm:text-2xl text-[#5C3A2E] font-semibold">What You'll Learn</h2>
            </div>
            <div className="text-[#2B2B2B]/70 text-sm leading-relaxed whitespace-pre-wrap">{mod.what_youll_learn}</div>
          </div>
        )}

        {/* Resources & Materials */}
        {((mod.materials && mod.materials.length > 0) || mod.syllabus_path) && (
          <div data-testid="module-resources-section" className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[#5C3A2E]" />
              <h2 className="font-heading text-xl sm:text-2xl text-[#5C3A2E] font-semibold">Resources & Materials</h2>
            </div>

            {mod.syllabus_path && (
              <a
                href={`${API_BASE}/api/files/${mod.syllabus_path}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="module-syllabus-link"
                className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF] hover:bg-[#EADBC8]/40 transition-colors mb-3"
              >
                <Download className="w-4 h-4 text-[#5C3A2E]" />
                <span className="text-sm text-[#5C3A2E] font-medium">{mod.syllabus_filename || "Course Syllabus (PDF)"}</span>
              </a>
            )}

            <div className="space-y-2">
              {mod.materials?.map((mat, i) => (
                <a
                  key={mat.id || i}
                  href={mat.type === "link" ? mat.url : `${API_BASE}/api/files/${mat.storage_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF] hover:bg-[#EADBC8]/40 transition-colors"
                >
                  {mat.type === "link" ? (
                    <ExternalLink className="w-4 h-4 text-[#C6A969]" />
                  ) : (
                    <Download className="w-4 h-4 text-[#5C3A2E]" />
                  )}
                  <span className="text-sm text-[#2B2B2B]">{mat.name || mat.original_filename || "Resource"}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Notes from Instructor */}
        {mod.notes && (
          <div data-testid="module-notes-section" className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <StickyNote className="w-5 h-5 text-[#5C3A2E]" />
              <h2 className="font-heading text-xl sm:text-2xl text-[#5C3A2E] font-semibold">Notes from Instructor</h2>
            </div>
            <div className="text-[#2B2B2B]/70 text-sm leading-relaxed prose prose-sm max-w-none [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#5C3A2E] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mod.notes) }} />
          </div>
        )}

        {/* Video Tutorials */}
        {mod.video_links && mod.video_links.length > 0 && (
          <div data-testid="module-videos-section" className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Video className="w-5 h-5 text-[#5C3A2E]" />
              <h2 className="font-heading text-xl sm:text-2xl text-[#5C3A2E] font-semibold">Video Tutorials</h2>
            </div>
            <div className="space-y-3">
              {mod.video_links.map((v, i) => (
                <a key={v.id || i} href={v.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF] hover:bg-[#EADBC8]/40 transition-colors">
                  <Video className="w-4 h-4 text-[#C6A969]" />
                  <span className="text-sm text-[#2B2B2B]">{v.title || "Video"}</span>
                  <ExternalLink className="w-3 h-3 text-[#2B2B2B]/30 ml-auto" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Image Gallery */}
        {mod.images && mod.images.length > 0 && (
          <div data-testid="module-gallery-section" className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Image className="w-5 h-5 text-[#5C3A2E]" />
              <h2 className="font-heading text-xl sm:text-2xl text-[#5C3A2E] font-semibold">Gallery</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {mod.images.map((img, i) => (
                <div key={img.id || i} className="aspect-square rounded-xl overflow-hidden bg-[#F7F3EF]">
                  <img
                    src={`${API_BASE}/api/files/${img.storage_path}`}
                    alt={img.original_filename || "Gallery"}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
}
